import React, { useCallback, useEffect, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Printer, Search, FileDown, ImagePlus, X, FileSignature, ChevronDown, Send, FileUp, CheckCircle2, CircleDot, CircleCheck, Paperclip } from "lucide-react";
import { exportRecordsToPDF, exportBehaviorContractPDF } from "@/lib/pdf";
import { academicYearOf } from "@/lib/dates";
import { useAuth } from "@/contexts/AuthContext";
import SchoolDocumentHeader from "@/components/SchoolDocumentHeader";
import CaseStudyFollowups from "@/components/CaseStudyFollowups";
import { academicFollowups } from "@/lib/caseStudy";
import PrintSignatures from "@/components/PrintSignatures";
import RecordApproval from "@/components/RecordApproval";
import { staffTitles } from "@/lib/staffTitles";
import { MAX_FILE_BYTES, MAX_TOTAL_BYTES, DRIVE_HINT, compressImage } from "@/lib/uploadLimits";
import { gradesForSchool, normalizeGrade, programBeneficiaryText } from "@/lib/sectionConfigs";

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result); r.onerror = reject; r.readAsDataURL(file);
  });
}

const initialForm = (fields) =>
  fields.reduce((a, f) => ({ ...a, [f.name]: ["photos", "checks", "attachments", "case_followups"].includes(f.type) ? [] : f.defaultValue || "" }), {});

const fmtVal = (v) => {
  if (Array.isArray(v)) return v.length ? v.join("، ") : "—";
  return v || "—";
};

const isOtherOption = (value) => /(?:أخرى|اخرى|آخر|اخر)/.test(value || "");

export function StudentPicker({
  students,
  directory,
  selectedIds,
  onChange,
  multiple,
  open,
  setOpen,
  search,
  setSearch,
  placeholder,
  emptyMessage,
  radioName,
  testId,
}) {
  const selectedStudents = directory.filter((student) => selectedIds.includes(student.student_id));
  const toggleStudent = (studentId) => {
    onChange(multiple
      ? selectedIds.includes(studentId)
        ? selectedIds.filter((id) => id !== studentId)
        : [...selectedIds, studentId]
      : [studentId]);
    setOpen(false);
  };

  if (!open) {
    return (
      <div className="space-y-3 rounded-xl border border-[#e6e2d8] bg-[#F9F8F6]/60 p-3">
        {selectedStudents.length ? (
          <div className="max-h-48 space-y-1 overflow-y-auto">
            {selectedStudents.map((student) => (
              <div key={student.student_id} className="flex items-center gap-2 rounded-lg bg-white px-2 py-1.5 text-sm">
                {multiple && (
                  <Checkbox
                    checked
                    aria-label={`إزالة ${student.student_name}`}
                    onCheckedChange={() => onChange(selectedIds.filter((id) => id !== student.student_id))}
                  />
                )}
                <span>{student.student_name}</span>
                <span className="ms-auto text-xs text-[#4A554E]">{student.grade}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#4A554E]">لم يتم اختيار طالب بعد.</p>
        )}
        <Button type="button" variant="outline" onClick={() => setOpen(true)} className="rounded-lg">
          {selectedStudents.length ? "إضافة أو تعديل أسماء الطلاب" : "اختيار أسماء الطلاب"}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border border-[#e6e2d8] bg-[#F9F8F6]/60 p-3">
      <Input
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder={placeholder}
        className="rounded-lg bg-white"
      />
      <div className="max-h-48 space-y-1 overflow-y-auto">
        {students.map((student) => (
          <label key={student.student_id} className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-white">
            {multiple ? (
              <Checkbox
                checked={selectedIds.includes(student.student_id)}
                onCheckedChange={() => toggleStudent(student.student_id)}
                data-testid={testId ? `${testId}-${student.student_id}` : undefined}
              />
            ) : (
              <input
                type="radio"
                name={radioName}
                checked={selectedIds.includes(student.student_id)}
                onChange={() => toggleStudent(student.student_id)}
                data-testid={testId ? `${testId}-${student.student_id}` : undefined}
              />
            )}
            <span>{student.student_name}</span>
            <span className="ms-auto text-xs text-[#4A554E]">{student.grade}</span>
          </label>
        ))}
        {!students.length && <p className="p-2 text-sm text-[#4A554E]">{emptyMessage}</p>}
      </div>
      <p className="text-xs text-[#4A554E]">تم اختيار {selectedIds.length} طالب.</p>
      <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-lg">
        إغلاق قائمة الطلاب
      </Button>
    </div>
  );
}

const WEEK_UNITS = { "الأول": 1, "الاول": 1, "الثاني": 2, "الثالث": 3, "الرابع": 4, "الخامس": 5, "السادس": 6, "السابع": 7, "الثامن": 8, "التاسع": 9, "العاشر": 10, "الحادي": 1, "الثانى": 2 };
export const weekOrdinal = (value) => {
  const text = String(value || "").trim();
  const digits = text.match(/\d+/);
  if (digits) return Number(digits[0]);
  if (text.startsWith("العشرون") || text.startsWith("العشرين")) return 20;
  const parts = text.split(/\s+/);
  if (parts[0] in WEEK_UNITS && parts[1] === "عشر") return 10 + WEEK_UNITS[parts[0]];
  if (parts[0] in WEEK_UNITS) return WEEK_UNITS[parts[0]];
  return 999;
};

// إجراءات إضافية في المواقف اليومية لقسم درة الشرق بنات فقط
const DURAT_GIRLS_INCIDENT_ACTIONS = [
  "احتواء الحالة لمعرفة سبب الإهمال",
  "توجيه الطلاب بالعلاج العقلاني وما يترتب عليه من تأخر دراسي وتدني في المستوى التعليمي",
  "توجيه الطلاب عن طريق النمذجة بضرب أمثلة تحفيزية لتعزيز الجانب الإيجابي",
  "توجيه الطلاب باستخدام استراتيجية التصحيح البسيط والاعتراف بالخطأ والاعتذار للمعلمة",
  "إخطار ولي الأمر بالاتصال لإكمال الواجبات وإحضار الكتاب والمتابعة مع الطالبة",
];

export default function RecordSection({ config, readOnly = false, counselorId = null, counselorName = "", counselorGender = "", schoolKey = null, schoolName = "", pdfFooter = {}, submittable = false, onRecordsChange = null }) {
  const { user } = useAuth();
  readOnly = readOnly || user?.role !== "counselor" || Boolean(counselorId && counselorId !== user?.id);
  const ownerGender = counselorGender || pdfFooter.counselorGender || (!counselorId && user?.role === "counselor" ? user.gender : "");
  const titles = staffTitles(ownerGender);
  const activeSchoolKey = schoolKey || user?.school_key;
  const [pdfScan, setPdfScan] = useState({ open: false, loading: false, saving: false, matches: [], selected: {} });
  const scanSpecialCasesPdf = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setPdfScan({ open: true, loading: true, saving: false, matches: [], selected: {} });
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/special-cases/pdf-scan", body, { headers: { "Content-Type": "multipart/form-data" } });
      const selected = {};
      data.matches.forEach((match) => { selected[match.student_id] = !match.exists; });
      setPdfScan({ open: true, loading: false, saving: false, matches: data.matches, selected });
    } catch (error) {
      setPdfScan({ open: false, loading: false, saving: false, matches: [], selected: {} });
      toast.error(formatErr(error));
    }
  };
  const saveScannedSpecialCases = async () => {
    const chosen = pdfScan.matches.filter((match) => pdfScan.selected[match.student_id]);
    if (!chosen.length) { toast.error("اختر طالبًا واحدًا على الأقل"); return; }
    setPdfScan((current) => ({ ...current, saving: true }));
    let saved = 0;
    for (const match of chosen) {
      try {
        await api.post("/records", { type: "special_case", data: {
          student_id: match.student_id, student: match.student, grade: match.grade,
          health: match.health.length ? match.health : [], handling: [], notes: match.notes,
        } });
        saved += 1;
      } catch (error) {
        toast.error(`${match.student}: ${formatErr(error)}`);
      }
    }
    toast.success(`تمت إضافة ${saved} حالة من الملف`);
    setPdfScan({ open: false, loading: false, saving: false, matches: [], selected: {} });
    load();
  };
  const checkOptions = (field) => {
    const base = field.options || [];
    if (!(config.key === "daily_incident" && field.name === "actions" && activeSchoolKey === "durat" && ownerGender === "female")) return base;
    const other = base.filter((o) => o === "إجراء آخر");
    return [...base.filter((o) => o !== "إجراء آخر"), ...DURAT_GIRLS_INCIDENT_ACTIONS, ...other];
  };
  const activeSchoolName = schoolName || user?.school_name || "";
  const printCounselorName = counselorName || pdfFooter.counselor || (user?.role === "counselor" ? user.name : "");
  const [colleagues, setColleagues] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [printRecord, setPrintRecord] = useState(null);
  const [principalName, setPrincipalName] = useState(pdfFooter.principal || "");
  const [principalGender, setPrincipalGender] = useState("");
  const [form, setForm] = useState(initialForm(config.fields));
  const [search, setSearch] = useState("");
  const [year, setYear] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [studentDirectory, setStudentDirectory] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [studentSearch, setStudentSearch] = useState("");
  const [studentPickerOpen, setStudentPickerOpen] = useState(true);
  const isPlanTask = config.key === "plan_task" || config.key.startsWith("plan_task_");
  const isDailyIncident = config.key === "daily_incident";
  const isIndividualSession = config.key === "individual_session";
  const isGroupSession = config.key === "group_session";
  const isInfoForm = config.key === "info_form";
  const isSpecialCase = config.key === "special_case";
  const isGiftedStudent = config.key === "gifted_student";
  const isAcademicRecord = config.key === "academic_record";
  const isParentContact = config.key === "parent_contact";
  const isAbsence = config.key === "absence";
  const isProgram = config.key === "program";
  const isBehaviorContract = config.key === "behavior_contract";
  const usesStudentDirectory = isDailyIncident || isIndividualSession || isGroupSession || isInfoForm || isSpecialCase || isGiftedStudent || isParentContact || isAbsence || isBehaviorContract || isAcademicRecord;
  const usesSingleStudentDirectory = isIndividualSession || isAcademicRecord;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: config.key };
      if (counselorId) params.counselor_id = counselorId;
      let data;
      if (isAbsence) {
        const [absenceResponse, tardyResponse] = await Promise.all([
          api.get("/records", { params }),
          api.get("/records", { params: { ...params, type: "tardy" } }),
        ]);
        data = [
          ...absenceResponse.data,
          ...tardyResponse.data.map((record) => ({
            ...record,
            data: { ...record.data, attendance_type: "تأخر" },
          })),
        ].sort((left, right) => String(right.created_at).localeCompare(String(left.created_at)));
      } else {
        ({ data } = await api.get("/records", { params }));
      }
      setRecords(data);
      onRecordsChange?.(data.length);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  }, [config, counselorId, isAbsence, onRecordsChange]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (config.key !== "parent_contact" || readOnly || user?.role !== "counselor") return undefined;
    let cancelled = false;
    api.get("/counselors/colleagues")
      .then(({ data }) => { if (!cancelled) setColleagues(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setColleagues([]); });
    return () => { cancelled = true; };
  }, [config.key, readOnly, user?.role]);

  useEffect(() => {
    let active = true;
    const params = counselorId ? { counselor_id: counselorId } : {};
    api.get("/school-principal", { params })
      .then(({ data }) => {
        if (active) { setPrincipalName(pdfFooter.principal || data.name || ""); setPrincipalGender(data.gender || ""); }
      })
      .catch((error) => {
        if (active) toast.error(formatErr(error));
      });
    return () => { active = false; };
  }, [counselorId, pdfFooter.principal]);

  useEffect(() => {
    if (!printRecord) return undefined;
    const finishPrinting = () => setPrintRecord(null);
    document.body.classList.add("printing-record-form");
    window.addEventListener("afterprint", finishPrinting, { once: true });
    const timer = window.setTimeout(() => window.print(), 100);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("afterprint", finishPrinting);
      document.body.classList.remove("printing-record-form");
    };
  }, [printRecord]);

  useEffect(() => {
    if (!usesStudentDirectory) return;
    const params = { type: "student_record" };
    if (counselorId) params.counselor_id = counselorId;
    api.get("/records", { params })
      .then(({ data }) => setStudentDirectory(data.map((record) => ({
        ...record.data,
        grade: normalizeGrade(record.data.grade),
      }))))
      .catch((error) => toast.error(formatErr(error)));
  }, [counselorId, usesStudentDirectory]);

  const resetForm = () => {
    setForm(initialForm(config.fields));
    setEditingId(null);
    setSelectedGrades([]);
    setSelectedStudentIds([]);
    setSelectedStudentId("");
    setStudentSearch("");
    setStudentPickerOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const recordData = { ...form };
      if ((isInfoForm || isAcademicRecord) && (form.followups || []).some((row) => !row.date || !row.result)) {
        toast.error(isInfoForm ? "أكمل تاريخ ونتيجة كل متابعة للحالة." : "أكمل تاريخ ونتيجة كل متابعة في السجل الأكاديمي.");
        return;
      }
      if (isProgram) {
        const scope = form.beneficiary_scope;
        const legacy = editingId && (scope === "legacy" || !scope) && form.beneficiaries !== undefined && form.beneficiaries !== "";
        if (!legacy && !["all_school", "selected_grades"].includes(scope)) {
          toast.error("اختر جميع طلاب المدرسة أو صفوفاً محددة للمستفيدين.");
          return;
        }
        const selected = form.beneficiary_grades || [];
        if (scope === "selected_grades" && (!selected.length || selected.some((grade) => !gradesInRoster.includes(grade)))) {
          toast.error("اختر صفاً واحداً على الأقل من صفوف المدرسة.");
          return;
        }
        if (legacy) {
          delete recordData.beneficiary_scope;
          delete recordData.beneficiary_grades;
        } else {
          recordData.beneficiary_grades = scope === "selected_grades" ? gradesInRoster.filter((grade) => selected.includes(grade)) : [];
        }
      }
      for (const field of config.fields.filter((field) => field.customOther)) {
        const choices = form[field.name] || [];
        const selected = choices.includes("أخرى");
        const text = selected ? (form[`${field.name}_other`] || "").trim() : "";
        if (selected && !text) {
          toast.error(`أدخل النص عند اختيار «أخرى» في ${field.label}.`);
          return;
        }
        recordData[field.name] = [...new Set([...choices.filter((value) => value !== "أخرى"), ...(text ? [text] : [])])];
        recordData[`${field.name}_other`] = text;
      }
      if (editingId) {
        if (isBehaviorContract || isDailyIncident) {
          if (selectedStudentIds.length !== 1) {
            toast.error("اختر طالباً واحداً لتعديل هذا السجل؛ لإضافة عدة طلاب استخدم إضافة جديد");
            return;
          }
          const student = studentDirectory.find((item) => item.student_id === selectedStudentIds[0]);
          if (!student) {
            toast.error("اختر الطالب من السجلات المستوردة");
            return;
          }
          await api.put(`/records/${editingId}`, {
            data: {
              ...recordData, student: student.student_name, student_id: student.student_id, grade: student.grade,
              ...(isBehaviorContract ? { counselor: printCounselorName } : {}),
            },
          });
        } else if (isGroupSession) {
          const selectedStudents = studentDirectory.filter((student) => selectedStudentIds.includes(student.student_id));
          if (!selectedStudents.length) {
            toast.error("اختر طالباً واحداً على الأقل من السجلات");
            return;
          }
          await api.put(`/records/${editingId}`, {
            data: {
              ...form,
              student_ids: selectedStudents.map((student) => student.student_id),
              students: selectedStudents.map((student) => student.student_name),
              grade: [...new Set(selectedStudents.map((student) => student.grade))].join("، "),
              count: String(selectedStudents.length),
            },
          });
        } else if (usesSingleStudentDirectory || isInfoForm || isGiftedStudent || isParentContact || isAbsence || isSpecialCase) {
          const student = studentDirectory.find((item) => item.student_id === selectedStudentId);
          if (!student) {
            toast.error("اختر الطالب من السجلات المستوردة");
            return;
          }
          const parentPhone = student.father_phone || student.mother_phone || "";
          await api.put(`/records/${editingId}`, {
            data: {
              ...recordData,
              student: student.student_name,
              student_id: student.student_id,
              grade: student.grade,
              ...(isSpecialCase ? { phone: parentPhone } : {}),
            },
          });
        } else {
          await api.put(`/records/${editingId}`, { data: recordData });
        }
        toast.success("تم التحديث");
      } else if (isBehaviorContract) {
        const selectedStudents = studentDirectory.filter((student) => selectedStudentIds.includes(student.student_id));
        if (!selectedStudents.length) {
          toast.error("اختر طالباً واحداً على الأقل من السجلات");
          return;
        }
        await api.post("/records/bulk", {
          type: config.key,
          records: selectedStudents.map((student) => ({
            ...recordData, student: student.student_name, student_id: student.student_id,
            grade: student.grade, counselor: printCounselorName,
          })),
        });
        toast.success(`تم إنشاء ${selectedStudents.length} تعاقد سلوكي`);
      } else if (isDailyIncident) {
        const selectedStudents = studentDirectory.filter((student) => selectedStudentIds.includes(student.student_id));
        if (!selectedStudents.length) {
          toast.error("اختر طالباً واحداً على الأقل من السجلات");
          return;
        }
        const recordsToCreate = selectedStudents.map((student) => ({
          ...form,
          student: student.student_name,
          student_id: student.student_id,
          grade: student.grade,
        }));
        await api.post("/records/bulk", { type: config.key, records: recordsToCreate });
        toast.success(`تمت إضافة الموقف إلى سجلات ${selectedStudents.length} طالب`);
      } else if (isGroupSession) {
        const selectedStudents = studentDirectory.filter((student) => selectedStudentIds.includes(student.student_id));
        if (!selectedStudents.length) {
          toast.error("اختر طالباً واحداً على الأقل من السجلات");
          return;
        }
        await api.post("/records", {
          type: config.key,
          data: {
            ...form,
            student_ids: selectedStudents.map((student) => student.student_id),
            students: selectedStudents.map((student) => student.student_name),
            grade: [...new Set(selectedStudents.map((student) => student.grade))].join("، "),
            count: String(selectedStudents.length),
          },
        });
        toast.success(`تمت إضافة الجلسة لـ ${selectedStudents.length} طالب`);
      } else if (usesSingleStudentDirectory || isInfoForm || isSpecialCase || isGiftedStudent || isParentContact || isAbsence) {
        const student = studentDirectory.find((item) => item.student_id === selectedStudentId);
        if (!student) {
          toast.error("اختر الطالب من السجلات المستوردة");
          return;
        }
        const payload = { ...recordData, student: student.student_name, student_id: student.student_id, grade: student.grade };
        if (isSpecialCase) payload.phone = student.father_phone || student.mother_phone || "";
        if (editingId) await api.put(`/records/${editingId}`, { data: payload });
        else {
          const { data: saved } = await api.post("/records", { type: config.key, data: payload });
          if (isAcademicRecord && payload.category === "ضعيف") {
            if (!saved?.data?.case_study_id) throw new Error("حُفظ السجل الأكاديمي؛ تعذر فتح دراسة الحالة. راجع قسم دراسة الحالة قبل إعادة الإضافة.");
            window.location.assign(`/counselor/info-form?edit=${encodeURIComponent(saved.data.case_study_id)}`);
          }
        }
        toast.success(isAcademicRecord ? "تم حفظ السجل الأكاديمي" : isInfoForm
          ? "تمت إضافة دراسة الحالة"
          : isGiftedStudent
            ? "تمت إضافة سجل الطالب الموهوب"
            : isParentContact
              ? "تم تسجيل التواصل مع ولي الأمر"
              : isAbsence
                ? "تم تسجيل متابعة الغياب"
              : "تمت إضافة جلسة الإرشاد الفردي");
      } else {
        await api.post("/records", { type: config.key, data: recordData });
        toast.success("تمت الإضافة");
      }
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const edit = (r) => {
    setEditingId(r.id);
    setStudentPickerOpen(false);
    setStudentSearch("");
    const existingData = { ...r.data, grade: normalizeGrade(r.data.grade) };
    if (isAcademicRecord) existingData.followups = academicFollowups(r.data);
    if (isIndividualSession || isGroupSession || isInfoForm || isSpecialCase || isGiftedStudent || isParentContact || isProgram || isAbsence || isBehaviorContract) {
      const choiceFields = isSpecialCase ? ["health", "handling"]
        : isIndividualSession
          ? ["goals", "recommendations"]
          : isGroupSession
            ? ["goals", "recommendations", "students"]
            : isInfoForm
              ? ["problem", "negative", "positive"]
              : isGiftedStudent
                ? ["areas", "talent_details", "support_plan", "achievements"]
                : isParentContact
                  ? ["reason", "recommendations"]
                  : isAbsence
                    ? ["reason", "action"]
                    : isBehaviorContract
                      ? ["items", "reward", "noncompliance"]
                  : ["results"];
      choiceFields.forEach((field) => {
        if (existingData[field] && !Array.isArray(existingData[field])) {
          existingData[field] = [existingData[field]];
        }
      });
    }
    config.fields.filter((field) => field.customOther).forEach((field) => {
      const text = existingData[`${field.name}_other`];
      const values = Array.isArray(existingData[field.name]) ? existingData[field.name] : existingData[field.name] ? [existingData[field.name]] : [];
      existingData[field.name] = text
        ? [...values.filter((value) => value !== text && value !== "أخرى"), "أخرى"]
        : values;
    });
    setForm({ ...initialForm(config.fields), ...existingData });
    if (isDailyIncident) {
      setSelectedGrades(r.data.grade ? [normalizeGrade(r.data.grade)] : []);
      setSelectedStudentIds(r.data.student_id ? [r.data.student_id] : []);
    }
    if (isGroupSession) {
      const oldNames = Array.isArray(r.data.students) ? r.data.students : r.data.students ? [r.data.students] : [];
      const groupStudentIds = r.data.student_ids?.length ? r.data.student_ids : studentDirectory.filter((student) => oldNames.includes(student.student_name)).map((student) => student.student_id);
      const legacyStudents = [...new Set([...(r.data.legacy_students || []), ...oldNames.filter((name) => !studentDirectory.some((student) => groupStudentIds.includes(student.student_id) && student.student_name === name))])];
      const groupGrade = studentDirectory.find((student) => groupStudentIds.includes(student.student_id))?.grade || normalizeGrade(r.data.grade);
      setSelectedStudentIds(groupStudentIds);
      setForm((current) => ({ ...current, grade: groupGrade, legacy_students: legacyStudents }));
    }
    if (isBehaviorContract) {
      setSelectedStudentIds(r.data.student_id ? [r.data.student_id] : []);
      setSelectedGrades(r.data.grade ? [normalizeGrade(r.data.grade)] : []);
    }
    if (usesSingleStudentDirectory || isInfoForm || isSpecialCase || isGiftedStudent || isParentContact || isAbsence) {
      setSelectedStudentId(r.data.student_id || "");
    }
    setOpen(true);
  };

  const openedContact = React.useRef("");
  useEffect(() => {
    if ((!isParentContact && !isInfoForm) || readOnly || loading) return;
    const recordId = new URLSearchParams(window.location.search).get("edit");
    if (!recordId || openedContact.current === recordId) return;
    openedContact.current = recordId;
    const record = records.find((entry) => entry.id === recordId);
    if (record) edit(record);
    else toast.error("لم يتم العثور على السجل المطلوب ضمن سجلاتك");
  });

  const del = async (id) => {
    if (!window.confirm("هل أنت متأكد من الحذف؟")) return;
    try { await api.delete(`/records/${id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const submitForApproval = async (id) => {
    if (!window.confirm("إرسال هذا البند للاعتماد؟")) return;
    try { await api.post(`/records/${id}/submit`); toast.success("تم إرسال البند للاعتماد"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const print = () => {
    document.body.classList.add("printing-record-section");
    const cleanup = () => document.body.classList.remove("printing-record-section");
    window.addEventListener("afterprint", cleanup, { once: true });
    window.print();
  };
  const printForm = (record) => setPrintRecord(record);
  const printSignatures = (
    <PrintSignatures className="record-print-signatures text-sm" principalName={principalName} counselorName={printCounselorName} counselorGender={ownerGender} principalGender={principalGender} />
  );
  const exportPDF = async () => {
    try {
      await exportRecordsToPDF(config.title, config.columns, filtered, config.fields, {
        ...pdfFooter,
        counselor: printCounselorName,
        principal: principalName,
        counselorGender: ownerGender,
        principalGender,
        schoolKey: activeSchoolKey,
        schoolName: activeSchoolName,
      });
    } catch (error) {
      toast.error(formatErr(error));
    }
  };

  const filteredRaw = records.filter((r) => {
    if (year !== "all" && academicYearOf(r.created_at) !== year) return false;
    if (!search) return true;
    return JSON.stringify(r.data).toLowerCase().includes(search.toLowerCase());
  });
  const filtered = isPlanTask
    ? [...filteredRaw].sort((a, b) => {
      const diff = weekOrdinal(a.data?.week) - weekOrdinal(b.data?.week);
      return diff !== 0 ? diff : String(a.created_at).localeCompare(String(b.created_at));
    })
    : filteredRaw;
  const incidentGroups = isDailyIncident
    ? Object.values(filtered.reduce((groups, record) => {
      const key = record.data.student_id || `${record.data.student}|${record.data.grade}`;
      if (!groups[key]) groups[key] = { student: record.data.student, grade: record.data.grade, incidents: [] };
      groups[key].incidents.push(record.data);
      return groups;
    }, {})).sort((a, b) => String(a.student).localeCompare(String(b.student), "ar"))
    : [];
  const gradesInRoster = gradesForSchool(activeSchoolKey);
  const availableStudents = studentDirectory.filter((student) => (
    selectedGrades.includes(student.grade)
    && `${student.student_name} ${student.student_id}`.toLowerCase().includes(studentSearch.trim().toLowerCase())
  ));
  const counselingStudentResults = studentDirectory.filter((student) => (
    student.grade === form.grade
    && `${student.student_name} ${student.student_id} ${student.grade}`.toLowerCase().includes(studentSearch.trim().toLowerCase())
  ));
  const caseStudyStudents = studentDirectory.filter((student) => (
    student.grade === form.grade
    && `${student.student_name} ${student.student_id}`.toLowerCase().includes(studentSearch.trim().toLowerCase())
  ));
  const behaviorContractStudents = studentDirectory.filter((student) => (
    selectedGrades.includes(student.grade)
    && `${student.student_name} ${student.student_id}`.toLowerCase().includes(studentSearch.trim().toLowerCase())
  ));
  const renderFieldValue = (fieldName, value, recordData = {}) => {
    if ((isInfoForm || isAcademicRecord) && fieldName === "followups") {
      const field = config.fields.find((entry) => entry.name === fieldName);
      return <CaseStudyFollowups value={isAcademicRecord ? academicFollowups(recordData) : value} options={field.options} notesLabel={field.notesLabel} showDay={field.showDay} />;
    }
    if (isProgram && fieldName === "beneficiary_scope") return programBeneficiaryText(recordData);
    if (config.fields.some((field) => field.name === fieldName && field.customOther)) return fmtVal(value);
    if (isGroupSession && fieldName === "students" && recordData.legacy_students?.length) {
      return `${fmtVal(value)} — أسماء محفوظة من السجل السابق: ${recordData.legacy_students.join("، ")}`;
    }
    if (fieldName === "evidence_files" && Array.isArray(value)) {
      if (!value.length) return "—";
      return (
        <div className="flex flex-wrap gap-2">
          {value.map((file, index) => (
            <a
              key={`${file.name}-${index}`}
              href={file.data}
              download={file.name}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-lg border border-[#e6e2d8] px-2 py-1 text-xs text-[#3E6A56] hover:bg-[#3E6A56]/5"
            >
              <Paperclip className="h-3.5 w-3.5" />
              <span className="max-w-40 truncate">{file.name}</span>
            </a>
          ))}
        </div>
      );
    }
    if (isInfoForm && fieldName === "status") {
      const isClosed = value === "مغلقة";
      const StatusIcon = isClosed ? CircleCheck : CircleDot;
      return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${isClosed ? "bg-[#3E6A56]/10 text-[#2D4F40]" : "bg-[#DDA74A]/15 text-[#805B12]"}`}>
          <StatusIcon className="h-4 w-4" />
          {value || "مستمرة"}
        </span>
      );
    }
    const selectedOther = Array.isArray(value)
      ? value.some((item) => isOtherOption(item))
      : isOtherOption(value);
    const otherText = selectedOther ? recordData[`${fieldName}_other`] : "";
    return otherText ? `${fmtVal(value)}: ${otherText}` : fmtVal(value);
  };
  const years = [...new Set(records.map((r) => academicYearOf(r.created_at)))].sort().reverse();

  const RowActions = ({ r }) => (
    <div className="flex gap-1">
      {isAcademicRecord && r.data.category === "ضعيف" && r.data.case_study_id && (
        <a className="text-xs text-[#3E6A56] underline" href={`/counselor/info-form?edit=${encodeURIComponent(r.data.case_study_id)}`}>استكمال دراسة الحالة</a>
      )}
      {config.key === "behavior_contract" && (
        <Button size="sm" variant="ghost" onClick={() => exportBehaviorContractPDF(r.data, activeSchoolKey, { counselor: printCounselorName, principal: principalName, counselorGender: ownerGender, principalGender })} className="h-8 w-8 p-0 text-[#C97052] hover:bg-[#C97052]/10" title="طباعة التعاقد" data-testid={`contract-print-${r.id}`}>
          <FileSignature className="w-4 h-4" />
        </Button>
      )}
      <Button size="sm" variant="ghost" onClick={() => edit(r)} className="h-8 w-8 p-0 text-[#3E6A56] hover:bg-[#3E6A56]/10" data-testid={`${config.key}-edit-${r.id}`}>
        <Pencil className="w-4 h-4" />
      </Button>
      <Button size="sm" variant="ghost" onClick={() => del(r.id)} className="h-8 w-8 p-0 text-[#B84B4B] hover:bg-[#B84B4B]/10" data-testid={`${config.key}-delete-${r.id}`}>
        <Trash2 className="w-4 h-4" />
      </Button>
    </div>
  );

  const labelOf = (c) => config.fields.find((x) => x.name === c)?.label || c;

  return (
    <div className={`record-section-printable space-y-6 ${isPlanTask ? "record-section-weekly-plan" : ""}`}>
      <RecordApproval section={config.key} counselorId={counselorId} counselorGender={ownerGender} contentCount={records.length} />
      {printRecord && (
        <article className="single-record-print" dir="rtl">
          <SchoolDocumentHeader schoolKey={activeSchoolKey} gender={ownerGender} schoolName={activeSchoolName} />
          <h1 className="record-print-title mb-5 border-b-2 border-[#3E6A56] pb-3 text-center text-2xl font-bold text-[#1A201C]">
            {config.title}
          </h1>
          <div className="space-y-3">
            {config.fields.map((field) => {
              const value = printRecord.data[field.name];
              let printableValue = renderFieldValue(field.name, value, printRecord.data);
              if (field.type === "photos") {
                printableValue = Array.isArray(value) && value.length
                  ? <div className="flex flex-wrap gap-3">{value.map((image, index) => <img key={`${field.name}-${index}`} src={image} alt={`${field.label} ${index + 1}`} className="max-h-56 max-w-full object-contain" />)}</div>
                  : "—";
              } else if (field.type === "attachments") {
                printableValue = Array.isArray(value) && value.length
                  ? <div className="space-y-2">{value.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="flex flex-wrap items-center gap-2">
                      {file.type?.startsWith("image/") && <img src={file.data} alt={file.name} className="max-h-48 max-w-full object-contain" />}
                      <span>{file.name || "ملف مرفق"}{file.type ? ` (${file.type})` : ""}</span>
                    </div>
                  ))}</div>
                  : "—";
              }
              return (
                <section key={field.name} className="single-record-print-field">
                  <h2 className="mb-1 font-bold text-[#1A201C]">{field.label}</h2>
                  <div className="whitespace-pre-wrap break-words text-[#303832]">{printableValue}</div>
                </section>
              );
            })}
          </div>
          {printSignatures}
        </article>
      )}
      <div className="record-print-header print-only mb-4">
        <SchoolDocumentHeader schoolKey={activeSchoolKey} gender={ownerGender} schoolName={activeSchoolName} />
      </div>
      <div className="record-section-heading flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="record-print-title text-2xl font-bold text-[#1A201C]" style={{ fontFamily: "IBM Plex Sans Arabic" }}>
            {config.title}
          </h2>
          <p className="no-print text-sm text-[#4A554E] mt-1">
            {readOnly ? "عرض السجلات (للمشرف)" : "إضافة وإدارة السجلات الخاصة بك"}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 no-print">
          <div className="relative">
            <Search className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-[#4A554E]" />
            <Input
              placeholder={isDailyIncident || isGroupSession ? "بحث باسم الطالب أو الصف..." : "بحث..."}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="ps-10 bg-white rounded-xl border-[#e6e2d8] w-full sm:w-56"
              data-testid={`${config.key}-search-input`}
            />
          </div>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="rounded-xl bg-white border-[#e6e2d8] w-40" data-testid={`${config.key}-year-filter`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">كل الأعوام</SelectItem>
              {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={exportPDF} variant="outline" className="rounded-xl border-[#e6e2d8]" data-testid={`${config.key}-pdf-button`}>
            <FileDown className="w-4 h-4 me-2" /> PDF
          </Button>
          <Button onClick={print} variant="outline" className="rounded-xl border-[#e6e2d8]" data-testid={`${config.key}-print-list-button`}>
            <Printer className="w-4 h-4 me-2" /> {isPlanTask ? "طباعة الخطة" : "طباعة السجل"}
          </Button>
          {isSpecialCase && !readOnly && (
            <>
              <input id="special-case-pdf-input" type="file" accept="application/pdf,.pdf" className="hidden" onChange={scanSpecialCasesPdf} data-testid="special-case-pdf-input" />
              <Button type="button" variant="outline" className="rounded-xl border-[#e6e2d8]" onClick={() => document.getElementById("special-case-pdf-input")?.click()} data-testid="special-case-pdf-import-button">
                <FileUp className="w-4 h-4 me-2" /> استيراد من PDF
              </Button>
              <Dialog open={pdfScan.open} onOpenChange={(o) => { if (!o && !pdfScan.saving) setPdfScan({ open: false, loading: false, saving: false, matches: [], selected: {} }); }}>
                <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir="rtl">
                  <DialogHeader><DialogTitle>الحالات الموجودة في ملف PDF</DialogTitle></DialogHeader>
                  {pdfScan.loading ? (
                    <p className="py-8 text-center text-sm text-[#4A554E]">جاري قراءة الملف...</p>
                  ) : pdfScan.matches.length === 0 ? (
                    <p className="py-8 text-center text-sm text-[#4A554E]">لم يتم العثور على أسماء من سجلات طلابك في هذا الملف. تأكد أن أسماء الطلاب مستوردة وأن الملف نصي وليس صورة.</p>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-sm text-[#4A554E]">تم التعرف على {pdfScan.matches.length} طالب. اختر من تريد إضافته (الموجودون مسبقًا غير محددين).</p>
                      {pdfScan.matches.map((match) => (
                        <label key={match.student_id} className="flex items-start gap-3 rounded-xl border border-[#e6e2d8] bg-white p-3 text-sm">
                          <Checkbox checked={Boolean(pdfScan.selected[match.student_id])} onCheckedChange={(checked) => setPdfScan((current) => ({ ...current, selected: { ...current.selected, [match.student_id]: Boolean(checked) } }))} />
                          <span className="flex-1">
                            <strong>{match.student}</strong> <span className="text-[#4A554E]">({match.grade})</span>
                            {match.exists && <span className="ms-2 rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">مضاف مسبقًا</span>}
                            <span className="mt-1 block text-xs text-[#4A554E]">{match.health.length ? `الحالة الصحية: ${match.health.join("، ")}` : "لم تُحدَّد الحالة الصحية تلقائيًا — يمكن تعديلها بعد الإضافة"}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                  <DialogFooter>
                    <Button variant="outline" disabled={pdfScan.saving} onClick={() => setPdfScan({ open: false, loading: false, saving: false, matches: [], selected: {} })}>إلغاء</Button>
                    {pdfScan.matches.length > 0 && <Button disabled={pdfScan.saving} onClick={saveScannedSpecialCases} className="bg-[#3E6A56] hover:bg-[#2D4F40]">{pdfScan.saving ? "جاري الحفظ..." : "إضافة المحدد"}</Button>}
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </>
          )}
          {!readOnly && (
            <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]" data-testid={`${config.key}-add-button`}>
                  <Plus className="w-4 h-4 me-2" /> إضافة جديد
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir="rtl">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: "IBM Plex Sans Arabic" }}>
                    {editingId ? "تعديل" : "إضافة"} — {config.title}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                  {isBehaviorContract && (
                    <div className="sm:col-span-2 rounded-xl border border-[#e6e2d8] bg-[#F9F8F6] p-4 text-sm leading-7">
                      <p className="font-bold text-[#1A201C]">تعاقد سلوكي بين {titles.counselor} والطالب</p>
                      <p className="text-[#4A554E]">{titles.counselor}: <span className="font-semibold text-[#3E6A56]">{printCounselorName || "—"}</span></p>
                    </div>
                  )}
                  {config.fields.filter((f) => (!isAcademicRecord || f.name !== "honors" || form.category === "متفوق") && (!f.academicCategory || form.category === f.academicCategory) && (f.type !== "case_followups" || !isInfoForm || form.status === "مستمرة")).map((f) => (
                    <div key={f.name} className={["textarea", "checks", "case_followups"].includes(f.type) ? "sm:col-span-2" : ""}>
                      <Label className="mb-1.5 block text-sm">{f.label}{f.required && <span className="text-[#B84B4B]">*</span>}</Label>
                      {f.type === "case_followups" ? (
                        <CaseStudyFollowups value={form[f.name]} options={f.options} notesLabel={f.notesLabel} showDay={f.showDay} onChange={(value) => setForm((current) => ({ ...current, [f.name]: value }))} />
                      ) : f.type === "beneficiary_target" ? (
                        <div className="space-y-3">
                          <Select value={form.beneficiary_scope || (editingId && form.beneficiaries !== undefined && form.beneficiaries !== "" ? "legacy" : "")}
                            onValueChange={(value) => setForm((current) => ({ ...current, beneficiary_scope: value }))}>
                            <SelectTrigger aria-label="المستفيدون"><SelectValue placeholder="اختر المستفيدين" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all_school">جميع طلاب المدرسة</SelectItem>
                              <SelectItem value="selected_grades">صفوف محددة</SelectItem>
                              {editingId && form.beneficiaries !== undefined && form.beneficiaries !== "" && <SelectItem value="legacy">{programBeneficiaryText({ beneficiaries: form.beneficiaries })}</SelectItem>}
                            </SelectContent>
                          </Select>
                          {form.beneficiary_scope === "selected_grades" && <div className="flex flex-wrap gap-3">
                            {gradesInRoster.map((grade) => <label key={grade} className="flex items-center gap-2 text-sm">
                              <Checkbox data-testid={`program-beneficiary-grade-${grade}`} checked={(form.beneficiary_grades || []).includes(grade)}
                                onCheckedChange={(checked) => setForm((current) => ({
                                  ...current,
                                  beneficiary_grades: checked ? [...(current.beneficiary_grades || []), grade] : (current.beneficiary_grades || []).filter((value) => value !== grade),
                                }))} />
                              {grade}
                            </label>)}
                          </div>}
                        </div>
                      ) : (isGroupSession || usesSingleStudentDirectory) && f.name === "grade" ? (
                        <Select
                          value={form.grade || ""}
                          onValueChange={(grade) => {
                            setForm((current) => ({
                              ...current,
                              grade,
                              student: "",
                              student_id: "",
                            }));
                            setSelectedStudentId("");
                            if (!isGroupSession) setSelectedStudentIds([]);
                            setStudentSearch("");
                            setStudentPickerOpen(true);
                          }}
                        >
                          <SelectTrigger className="rounded-xl bg-white border-[#e6e2d8]"><SelectValue placeholder="اختر الصف أولاً" /></SelectTrigger>
                          <SelectContent>
                            {gradesInRoster.map((grade) => <SelectItem key={grade} value={grade}>{grade}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : isGroupSession && f.name === "students" ? (
                        !form.grade ? (
                          <p className="text-sm text-[#4A554E]">اختر الصف أولاً لعرض الطلاب المشاركين.</p>
                        ) : (
                          <StudentPicker
                            students={counselingStudentResults}
                            directory={studentDirectory}
                            selectedIds={selectedStudentIds}
                            onChange={setSelectedStudentIds}
                            multiple
                            open={studentPickerOpen}
                            setOpen={setStudentPickerOpen}
                            search={studentSearch}
                            setSearch={setStudentSearch}
                            placeholder="ابحث باسم الطالب أو رقمه"
                            emptyMessage={studentDirectory.length ? "لا يوجد طالب مطابق في الصف المختار." : "لا توجد سجلات طلاب؛ استورد ملف Excel أولاً."}
                            testId={`${config.key}-student`}
                          />
                        )
                      ) : isGroupSession && f.name === "count" ? (
                        <div>
                          <Input value={String(selectedStudentIds.length)} readOnly />
                          {form.legacy_students?.length > 0 && <p className="text-sm">أسماء محفوظة من السجل السابق: {form.legacy_students.join("، ")}</p>}
                        </div>
                      ) : isBehaviorContract && f.name === "grade" ? (
                        <div className="flex flex-wrap gap-x-5 gap-y-2 rounded-xl border border-[#e6e2d8] bg-[#F9F8F6]/60 p-3">
                          {gradesInRoster.map((grade) => (
                            <label key={grade} className="flex items-center gap-2 text-sm">
                              <Checkbox
                                checked={selectedGrades.includes(grade)}
                                onCheckedChange={(checked) => {
                                  const nextGrades = checked
                                    ? [...selectedGrades, grade]
                                    : selectedGrades.filter((item) => item !== grade);
                                  setSelectedGrades(nextGrades);
                                  setSelectedStudentIds((current) => current.filter((id) => (
                                    studentDirectory.some((student) => student.student_id === id && nextGrades.includes(student.grade))
                                  )));
                                  setStudentSearch("");
                                  setStudentPickerOpen(true);
                                }}
                              />
                              {grade}
                            </label>
                          ))}
                          {!gradesInRoster.length && <p className="text-sm text-[#4A554E]">لا توجد صفوف؛ استورد سجلات الطلاب أولاً.</p>}
                        </div>
                      ) : isBehaviorContract && f.name === "student" ? (
                        !selectedGrades.length ? (
                          <p className="text-sm text-[#4A554E]">اختر صفاً واحداً أو أكثر لعرض أسمائهم.</p>
                        ) : (
                        <StudentPicker
                          students={behaviorContractStudents}
                          directory={studentDirectory}
                          selectedIds={selectedStudentIds}
                          onChange={setSelectedStudentIds}
                          multiple={!editingId}
                          open={studentPickerOpen}
                          setOpen={setStudentPickerOpen}
                          search={studentSearch}
                          setSearch={setStudentSearch}
                          placeholder="ابحث باسم الطالب أو رقمه"
                          emptyMessage={selectedGrades.length ? "لا يوجد طلاب مطابقون في الصفوف المختارة." : "اختر صفاً أو أكثر لعرض الطلاب."}
                          radioName="behavior-contract-student"
                          testId={`${config.key}-student`}
                        />
                        )
                      ) : (isInfoForm || isSpecialCase || isGiftedStudent || isParentContact || isAbsence) && f.name === "grade" ? (
                        <Select
                          value={form.grade || ""}
                          onValueChange={(grade) => {
                            setForm((current) => ({ ...current, grade, student: "", student_id: "", ...(isSpecialCase ? { phone: "" } : {}) }));
                            setSelectedStudentId("");
                            setStudentSearch("");
                            setStudentPickerOpen(true);
                          }}
                        >
                          <SelectTrigger className="rounded-xl bg-white border-[#e6e2d8]"><SelectValue placeholder="اختر الصف" /></SelectTrigger>
                          <SelectContent>
                            {gradesInRoster.map((grade) => <SelectItem key={grade} value={grade}>{grade}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      ) : (isInfoForm || isSpecialCase || isGiftedStudent || isParentContact || isAbsence) && f.name === "student" ? (
                        !form.grade ? (
                          <p className="text-sm text-[#4A554E]">اختر الصف أولاً لعرض الطلاب المسجلين فيه.</p>
                        ) : (
                          <StudentPicker
                            students={caseStudyStudents}
                            directory={studentDirectory}
                            selectedIds={selectedStudentId ? [selectedStudentId] : []}
                            onChange={(ids) => {
                              const student = studentDirectory.find((item) => item.student_id === ids[0]);
                              setSelectedStudentId(ids[0] || "");
                              if (student) {
                                setForm((current) => ({
                                  ...current,
                                  student: student.student_name,
                                  student_id: student.student_id,
                                  ...(isSpecialCase ? { phone: student.father_phone || student.mother_phone || "" } : {}),
                                }));
                              }
                            }}
                            multiple={false}
                            open={studentPickerOpen}
                            setOpen={setStudentPickerOpen}
                            search={studentSearch}
                            setSearch={setStudentSearch}
                            placeholder="ابحث باسم الطالب أو رقمه"
                            emptyMessage={studentDirectory.length ? "لا يوجد طلاب مطابقون في الصف المختار." : "لا توجد سجلات طلاب؛ استورد ملف Excel أولاً."}
                            radioName={`${config.key}-student`}
                            testId={`${config.key}-student`}
                          />
                        )
                      ) : usesSingleStudentDirectory && f.name === "student" ? (
                        !form.grade ? (
                          <p className="text-sm text-[#4A554E]">اختر الصف أولاً لعرض الطلاب المسجلين فيه.</p>
                        ) : (
                        <StudentPicker
                          students={counselingStudentResults}
                          directory={studentDirectory}
                          selectedIds={selectedStudentId ? [selectedStudentId] : []}
                          onChange={(ids) => {
                            const student = studentDirectory.find((item) => item.student_id === ids[0]);
                            setSelectedStudentId(ids[0] || "");
                            if (student) {
                              setForm((current) => ({
                                ...current,
                                student: student.student_name,
                                student_id: student.student_id,
                                grade: student.grade,
                              }));
                            }
                          }}
                          multiple={false}
                          open={studentPickerOpen}
                          setOpen={setStudentPickerOpen}
                          search={studentSearch}
                          setSearch={setStudentSearch}
                          placeholder="ابحث باسم الطالب أو رقمه أو صفه"
                          emptyMessage={studentDirectory.length ? "لا يوجد طالب مطابق للبحث." : "لا توجد سجلات طلاب؛ استورد ملف Excel أولاً."}
                          radioName={`${config.key}-student`}
                          testId={`${config.key}-student`}
                        />
                        )
                      ) : f.type === "caller" ? (
                        colleagues.length > 1 ? (
                          <Select value={form.caller_id || user?.id || ""}
                            onValueChange={(value) => {
                              const chosen = colleagues.find((c) => c.id === value);
                              setForm((current) => ({ ...current, caller_id: value, caller: chosen?.name || "" }));
                            }}>
                            <SelectTrigger aria-label="المتصل"><SelectValue placeholder="اختر المتصل" /></SelectTrigger>
                            <SelectContent>
                              {colleagues.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.id === user?.id ? " (أنا)" : ""}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (
                          <Input value={form.caller || user?.name || ""} readOnly className="rounded-xl bg-[#F9F8F6] border-[#e6e2d8]" />
                        )
                      ) : isSpecialCase && f.name === "phone" ? (
                        <Input
                          value={form.phone || ""}
                          readOnly
                          placeholder="يُعبأ تلقائياً من سجل الطالب"
                          className="rounded-xl bg-[#F9F8F6] border-[#e6e2d8]"
                        />
                      ) : isDailyIncident && f.name === "grade" ? (
                        <div className="space-y-2 rounded-xl border border-[#e6e2d8] bg-[#F9F8F6]/60 p-3">
                          {gradesInRoster.length ? (
                            <div className="flex flex-wrap gap-x-5 gap-y-2">
                              {gradesInRoster.map((grade) => (
                                <label key={grade} className="flex items-center gap-2 text-sm">
                                  <Checkbox
                                    checked={selectedGrades.includes(grade)}
                                    onCheckedChange={(checked) => {
                                      const nextGrades = checked
                                        ? [...selectedGrades, grade]
                                        : selectedGrades.filter((item) => item !== grade);
                                      setSelectedGrades(nextGrades);
                                      setSelectedStudentIds((current) => current.filter((id) => (
                                        studentDirectory.some((student) => student.student_id === id && nextGrades.includes(student.grade))
                                      )));
                                      setForm((current) => ({ ...current, grade: nextGrades }));
                                      setStudentSearch("");
                                      setStudentPickerOpen(true);
                                    }}
                                  />
                                  {grade}
                                </label>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-[#4A554E]">لا توجد صفوف؛ استورد سجلات الطلاب أولاً.</p>
                          )}
                        </div>
                      ) : isDailyIncident && f.name === "student" ? (
                        !selectedGrades.length ? (
                          <p className="text-sm text-[#4A554E]">اختر صفاً واحداً أو أكثر لعرض أسمائهم.</p>
                        ) : (
                          <StudentPicker
                            students={availableStudents}
                            directory={studentDirectory}
                            selectedIds={selectedStudentIds}
                            onChange={setSelectedStudentIds}
                            multiple={!editingId}
                            open={studentPickerOpen}
                            setOpen={setStudentPickerOpen}
                            search={studentSearch}
                            setSearch={setStudentSearch}
                            placeholder="ابحث باسم الطالب أو رقمه"
                            emptyMessage={studentDirectory.length ? "لا يوجد طلاب مطابقون للبحث في الصفوف المختارة." : "لا توجد سجلات طلاب؛ استورد ملف Excel أولاً."}
                            testId={`${config.key}-student`}
                          />
                        )
                      ) : f.type === "textarea" ? (
                        <Textarea
                          value={form[f.name] || ""}
                          onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                          required={f.required}
                          className="rounded-xl bg-white border-[#e6e2d8] min-h-[80px]"
                        />
                      ) : f.type === "checks" ? (
                        <>
                          <div className="flex flex-wrap gap-x-5 gap-y-2 bg-[#F9F8F6]/60 border border-[#e6e2d8] rounded-xl p-3" data-testid={`${config.key}-${f.name}-checks`}>
                            {(f.customOther || f.preserveChoices ? [...new Set([...checkOptions(f), ...(form[f.name] || [])])] : checkOptions(f)).map((o) => {
                              const arr = form[f.name] || [];
                              const checked = arr.includes(o);
                              return (
                                <label key={o} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                                  <Checkbox
                                    checked={checked}
                                    onCheckedChange={(v) => setForm((current) => {
                                      const currentOptions = current[f.name] || [];
                                      const nextOptions = v
                                        ? o === f.exclusiveOption ? [o] : [...currentOptions.filter((item) => item !== f.exclusiveOption), o]
                                        : currentOptions.filter((item) => item !== o);
                                      const next = { ...current, [f.name]: nextOptions };
                                      if (!f.customOther && !nextOptions.some(isOtherOption)) next[`${f.name}_other`] = "";
                                      return next;
                                    })}
                                    data-testid={`${config.key}-${f.name}-check-${o}`}
                                  />
                                  {o}
                                </label>
                              );
                            })}
                          </div>
                          {(f.customOther ? (form[f.name] || []).includes("أخرى") : (form[f.name] || []).some(isOtherOption)) && (
                            <Input
                              aria-label={`${f.label} — أخرى`}
                              data-testid={`${config.key}-${f.name}-other`}
                              value={form[`${f.name}_other`] || ""}
                              onChange={(event) => setForm((current) => ({ ...current, [`${f.name}_other`]: event.target.value }))}
                              placeholder="اكتب التفاصيل هنا"
                              className="mt-2 rounded-xl bg-white border-[#e6e2d8]"
                            />
                          )}
                        </>
                      ) : f.type === "photos" ? (
                        <div className="grid grid-cols-3 gap-2">
                          {(form[f.name] || []).map((src, i) => (
                            <div key={i} className="relative aspect-video rounded-xl overflow-hidden border border-[#e6e2d8] bg-[#F9F8F6]">
                              <img src={src} alt={`ph-${i}`} className="w-full h-full object-cover" />
                              <button type="button" onClick={() => setForm({ ...form, [f.name]: form[f.name].filter((_, idx) => idx !== i) })} className="absolute top-1 start-1 bg-white/95 rounded-full p-1 text-[#B84B4B] shadow"><X className="w-3 h-3" /></button>
                            </div>
                          ))}
                          {(form[f.name] || []).length < 3 && (
                            <label className="cursor-pointer aspect-video rounded-xl border-2 border-dashed border-[#e6e2d8] hover:border-[#3E6A56] hover:bg-[#3E6A56]/5 flex flex-col items-center justify-center text-[#4A554E] transition text-xs">
                              <ImagePlus className="w-4 h-4 mb-1" /> أضف صورة
                              <input type="file" accept="image/*" multiple className="hidden"
                                onChange={async (e) => {
                                  const files = Array.from(e.target.files || []);
                                  const existing = form[f.name] || [];
                                  if (existing.length + files.length > 3) { toast.error("الحد الأقصى 3 صور"); return; }
                                  const b64 = await Promise.all(files.map((file) => compressImage(file)));
                                  setForm({ ...form, [f.name]: [...existing, ...b64] });
                                  e.target.value = "";
                                }} />
                            </label>
                          )}
                        </div>
                      ) : f.type === "attachments" ? (
                        <div className="space-y-3 rounded-xl border border-[#e6e2d8] bg-[#F9F8F6]/60 p-3">
                          {(form[f.name] || []).length > 0 && (
                            <div className="space-y-2">
                              {form[f.name].map((file, index) => (
                                <div key={`${file.name}-${index}`} className="flex items-center gap-2 rounded-lg bg-white p-2 text-sm">
                                  <Paperclip className="h-4 w-4 shrink-0 text-[#3E6A56]" />
                                  <span className="min-w-0 flex-1 truncate">{file.name}</span>
                                  <span className="shrink-0 text-xs text-[#4A554E]">{file.type || "ملف"}</span>
                                  <button
                                    type="button"
                                    aria-label={`حذف المرفق ${file.name}`}
                                    onClick={() => setForm((current) => ({
                                      ...current,
                                      [f.name]: current[f.name].filter((_, itemIndex) => itemIndex !== index),
                                    }))}
                                    className="rounded-full p-1 text-[#B84B4B] hover:bg-[#B84B4B]/10"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#e6e2d8] bg-white px-3 py-4 text-sm text-[#4A554E] transition hover:border-[#3E6A56] hover:bg-[#3E6A56]/5">
                            <Paperclip className="h-4 w-4" />
                            إرفاق ملف مكتوب أو صوتي أو مرئي أو صورة
                            <input
                              type="file"
                              accept=".pdf,.doc,.docx,.txt,.rtf,.odt,.ppt,.pptx,.xls,.xlsx,.csv,.mp3,.wav,.m4a,.ogg,.mp4,.webm,.mov,.avi,.jpg,.jpeg,.png,.gif,.webp"
                              multiple
                              className="hidden"
                              onChange={async (event) => {
                                const files = Array.from(event.target.files || []);
                                const existing = form[f.name] || [];
                                const totalBytes = [...existing, ...files].reduce((total, file) => total + (file.size || 0), 0);
                                if (files.some((file) => (file.size || 0) > MAX_FILE_BYTES)) {
                                  toast.error(DRIVE_HINT);
                                  event.target.value = "";
                                  return;
                                }
                                if (totalBytes > MAX_TOTAL_BYTES) {
                                  toast.error("مجموع المرفقات كبير (الحد 4 ميجابايت). ارفع الملفات الكبيرة على قوقل درايف وضع الرابط في الملاحظات.");
                                  event.target.value = "";
                                  return;
                                }
                                try {
                                  const addedFiles = await Promise.all(files.map(async (file) => ({
                                    name: file.name,
                                    type: file.type,
                                    size: file.size,
                                    data: await fileToBase64(file),
                                  })));
                                  setForm((current) => ({ ...current, [f.name]: [...(current[f.name] || []), ...addedFiles] }));
                                } catch {
                                  toast.error("تعذر قراءة أحد الملفات المرفقة");
                                }
                                event.target.value = "";
                              }}
                            />
                          </label>
                          <p className="text-xs text-[#4A554E]">الحد الأقصى 2 ميجابايت للملف الواحد. الملفات الأكبر (فيديو مثلاً) ارفعها على قوقل درايف وضع الرابط في الملاحظات.</p>
                        </div>
                      ) : f.type === "select" ? (
                        <>
                          <Select
                            value={form[f.name] || ""}
                            onValueChange={(value) => setForm((current) => ({
                              ...current,
                              [f.name]: value,
                              ...(!isOtherOption(value) ? { [`${f.name}_other`]: "" } : {}),
                            }))}
                          >
                            <SelectTrigger className="rounded-xl bg-white border-[#e6e2d8]"><SelectValue placeholder="اختر" /></SelectTrigger>
                            <SelectContent>
                              {(f.name === "grade" ? gradesInRoster : f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {isOtherOption(form[f.name]) && (
                            <Input
                              value={form[`${f.name}_other`] || ""}
                              onChange={(event) => setForm((current) => ({ ...current, [`${f.name}_other`]: event.target.value }))}
                              placeholder="اكتب التفاصيل هنا"
                              className="mt-2 rounded-xl bg-white border-[#e6e2d8]"
                            />
                          )}
                        </>
                      ) : (
                        <Input
                          type={f.type}
                          value={form[f.name] || ""}
                          onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                          required={f.required}
                          className="rounded-xl bg-white border-[#e6e2d8]"
                        />
                      )}
                    </div>
                  ))}
                  <DialogFooter className="sm:col-span-2 gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-xl">إلغاء</Button>
                    <Button type="submit" className="rounded-xl bg-[#3E6A56] hover:bg-[#2D4F40]" data-testid={`${config.key}-save-button`}>
                      {editingId ? "حفظ التعديلات" : "إضافة"}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {isDailyIncident && (
        <div className="space-y-3 rounded-2xl border border-[#e6e2d8] bg-white p-5">
          <div>
            <h3 className="font-bold text-[#1A201C]">مواقف الطلاب مجمعة</h3>
            <p className="mt-1 text-xs text-[#4A554E]">استخدم البحث بالاسم أو الصف، وافتح الطالب لعرض جميع مواقفه.</p>
          </div>
          {incidentGroups.length ? (
            <div className="space-y-2">
              {incidentGroups.map((group) => (
                <details key={`${group.student}-${group.grade}`} className="rounded-xl border border-[#e6e2d8] p-3">
                  <summary className="cursor-pointer font-semibold text-[#1A201C]">
                    {group.student} — {group.grade || "—"} <span className="text-xs font-normal text-[#4A554E]">({group.incidents.length} موقف)</span>
                  </summary>
                  <div className="mt-3 space-y-2">
                    {group.incidents.map((incident, index) => (
                      <div key={`${incident.date}-${index}`} className="rounded-lg bg-[#F9F8F6] p-3 text-sm">
                        <div>{incident.date || "دون تاريخ"}{incident.day ? ` — ${incident.day}` : ""}{incident.type ? ` — ${incident.type}` : ""}</div>
                        <div className="mt-1">{fmtVal(incident.description)}</div>
                        {incident.actions?.length > 0 && <div className="mt-1 text-[#4A554E]">الإجراءات: {fmtVal(incident.actions)}</div>}
                      </div>
                    ))}
                  </div>
                </details>
              ))}
            </div>
          ) : <p className="text-sm text-[#4A554E]">لا توجد مواقف مطابقة.</p>}
        </div>
      )}

      {/* Mobile collapsible cards */}
      <div className="lg:hidden space-y-3 no-print record-mobile-cards" data-testid={`${config.key}-mobile-cards`}>
        {loading ? (
          <div className="text-center py-8 text-[#4A554E]">جاري التحميل...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-[#4A554E] bg-white rounded-2xl border border-[#e6e2d8]">📭 لا توجد سجلات بعد</div>
        ) : filtered.map((r, i) => {
          const isOpen = expanded === r.id;
          return (
            <div key={r.id} className="bg-white rounded-2xl border border-[#e6e2d8] shadow-[0_8px_30px_rgb(0,0,0,0.03)] overflow-hidden">
              <button
                type="button"
                onClick={() => setExpanded(isOpen ? null : r.id)}
                className="w-full flex items-center justify-between gap-3 p-4 text-start"
                data-testid={`${config.key}-card-toggle-${r.id}`}
              >
                <div className="min-w-0">
                  <div className="font-semibold text-[#1A201C] truncate">{fmtVal(r.data[config.columns[0]])}</div>
                  <div className="text-xs text-[#4A554E] mt-0.5 truncate">
                    {config.columns.slice(1, 3).map((c) => fmtVal(r.data[c])).filter((v) => v !== "—").join(" · ") || `سجل ${i + 1}`}
                  </div>
                </div>
                <ChevronDown className={`w-4 h-4 text-[#4A554E] shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
              </button>
              {isOpen && (
                <div className="px-4 pb-4 border-t border-[#f0ebe0] pt-3 space-y-2">
                  {config.fields.filter((f) => f.type !== "photos").map((f) => (
                    <div key={f.name} className="flex gap-2 text-sm">
                      <span className="text-[#4A554E] shrink-0 w-28">{f.label}:</span>
                      <span className="text-[#1A201C]">{renderFieldValue(f.name, r.data[f.name], r.data)}</span>
                    </div>
                  ))}
                  <div className="flex gap-1 pt-2">
                    <Button size="sm" variant="ghost" onClick={() => printForm(r)} className="h-8 w-8 p-0 text-[#3E6A56] hover:bg-[#3E6A56]/10" title="طباعة الاستمارة كاملة" aria-label="طباعة الاستمارة كاملة" data-testid={`${config.key}-print-form-${r.id}`}>
                      <Printer className="w-4 h-4" />
                    </Button>
                    {!readOnly && <RowActions r={r} />}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop table */}
      <div className={`hidden lg:block bg-white rounded-2xl border border-[#e6e2d8] shadow-[0_8px_30px_rgb(0,0,0,0.03)] overflow-hidden record-desktop-table ${isPlanTask ? "record-weekly-plan-table" : ""}`}>
        <div className={`record-table-scroll ${isPlanTask ? "overflow-x-hidden" : "overflow-x-auto"}`}>
          <table className={`w-full text-sm record-data-table ${isPlanTask ? "table-fixed" : ""}`}>
            {isPlanTask && (
              <colgroup>
                <col style={{ width: readOnly ? "4.5%" : "4%" }} />
                <col style={{ width: readOnly ? "8%" : "7%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: readOnly ? "16%" : "15%" }} />
                <col style={{ width: readOnly ? "12%" : "11%" }} />
                <col style={{ width: readOnly ? "21%" : "20%" }} />
                <col style={{ width: readOnly ? "8%" : "8%" }} />
                <col style={{ width: readOnly ? "8.5%" : "9%" }} />
                <col style={{ width: readOnly ? "12%" : "16%" }} />
              </colgroup>
            )}
            <thead className="bg-[#F9F8F6] text-[#1A201C]">
              <tr>
                <th className={`text-start px-4 py-3 font-semibold ${isPlanTask ? "" : "w-12"}`}>م</th>
                {config.columns.map((c) => (
                  <th key={c} className={`text-start px-4 py-3 font-semibold ${isPlanTask ? "whitespace-normal break-words" : "whitespace-nowrap"}`}>{labelOf(c)}</th>
                ))}
                <th className={`text-start px-4 py-3 font-semibold no-print ${isPlanTask ? "" : "w-32"}`}>الإجراءات</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={config.columns.length + 2} className="text-center py-8 text-[#4A554E]">جاري التحميل...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={config.columns.length + 2} className="text-center py-12 text-[#4A554E]">
                  📭 لا توجد سجلات بعد
                </td></tr>
              ) : filtered.map((r, i) => (
                <tr key={r.id} className="border-t border-[#f0ebe0] hover:bg-[#F9F8F6]/60 transition">
                  <td className={`px-4 py-3 text-[#4A554E] ${isPlanTask ? "align-top" : ""}`}>{i + 1}</td>
                  {config.columns.map((c) => (
                    <td key={c} className={`px-4 py-3 text-[#1A201C] ${isPlanTask ? "align-top whitespace-normal break-words" : "max-w-xs truncate"}`}>{renderFieldValue(c, r.data[c], r.data)}</td>
                  ))}
                  <td className={`px-4 py-3 no-print ${isPlanTask ? "align-top" : ""}`}>
                    <div className="flex flex-wrap gap-1">
                      <Button size="sm" variant="ghost" onClick={() => printForm(r)} className="h-8 w-8 p-0 text-[#3E6A56] hover:bg-[#3E6A56]/10" title="طباعة الاستمارة كاملة" aria-label="طباعة الاستمارة كاملة" data-testid={`${config.key}-print-form-${r.id}`}>
                        <Printer className="w-4 h-4" />
                      </Button>
                      {!readOnly && (
                        <RowActions r={r} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {printSignatures}
    </div>
  );
}
